import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AppUserService } from 'src/app/services/app-user.service';
import { AppSelectionService } from 'src/app/services/app-selection.service';

/**
 * Post-login resolution step, and the only place that decides where a freshly
 * signed-in user lands. Behind MsalGuard, so it always runs authenticated.
 *
 * It exists as a routed component rather than a guard or a resolver so that the
 * app-list lookup has somewhere to render: a spinner while it runs, and a real
 * error with a retry if it fails. Once the answer arrives it forwards — no apps
 * to /no-access, exactly one straight into the dashboard (single-app users never
 * see the picker), more than one to /select-app.
 *
 * Every forward replaces the URL, so this page never becomes a Back target.
 */
@Component({
  selector: 'bhub-app-loading',
  templateUrl: './app-loading.component.html',
  styleUrls: ['./app-loading.component.scss'],
  standalone: false
})
export class AppLoadingComponent implements OnInit, OnDestroy {
  error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private appUserService: AppUserService,
    private appSelection: AppSelectionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.resolveDestination();
  }

  retry(): void {
    this.error = null;
    this.resolveDestination();
  }

  private resolveDestination(): void {
    this.appUserService.getUserAppList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: apps => {
          const appList = apps ?? [];

          if (appList.length === 0) {
            this.router.navigate(['/no-access'], { replaceUrl: true });
          } else if (appList.length === 1) {
            // Single-app users never see a picker.
            this.appSelection.selectApplication(appList[0].applicationId);
          } else {
            this.router.navigate(['/select-app'], { replaceUrl: true });
          }
        },
        error: () => {
          this.error = 'We could not load your applications. Please try again.';
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
